import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';

// Your actual Firebase project configuration
// IMPORTANT: Replace these values with the ones from YOUR Firebase project settings.
const firebaseConfig = {
    apiKey: "AIzaSyD-2eEs8-bdPdbOx12nRCvzmUxrI2CaQng",
    authDomain: "roboshakti-textbook-linker.firebaseapp.com",
    projectId: "roboshakti-textbook-linker",
    storageBucket: "roboshakti-textbook-linker.firebasestorage.app",
    messagingSenderId: "469829099477",
    appId: "1:469829099477:web:754df1cbeb0e9f5b5ba75c",
    measurementId: "G-559ENY6CVX"
};

// Define a consistent app identifier for Firestore paths for your deployed app.
// This must be a simple string without special characters like colons or slashes.
// Use this EXACT string for both desktop and mobile apps.
const APP_IDENTIFIER = 'roboshakti-teachers-app';

// Since we are building locally for deployment, __initial_auth_token is not available.
// We'll set it to null, and the app will fall back to anonymous sign-in.
const initialAuthToken = null;

// Dynamically load html5-qrcode library for barcode scanning
const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(script);
        script.onerror = () => reject(new Error(`Script load error for ${src}`));
        document.body.appendChild(script);
    });
};

function App() {
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null); // This is the mobile's own user ID (anonymous)
    const [pairedDesktopUserId, setPairedDesktopUserId] = useState(''); // Stores the desktop's user ID after pairing
    const [desktopPairingCode, setDesktopPairingCode] = useState(''); // Stores the pairing code after pairing
    const [scanResult, setScanResult] = useState('');
    const [status, setStatus] = useState('Initializing...');
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isScannerReady, setIsScannerReady] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [manualPairingInput, setManualPairingInput] = useState('');

    const html5QrcodeScannerRef = useRef(null); // Ref for the html5-qrcode scanner instance
    const scannerId = "qr-code-reader"; // ID for the HTML element where the scanner will render

    // Function to show a custom modal message instead of alert()
    const showCustomModal = (message) => {
        setModalMessage(message);
        setShowModal(true);
    };

    // Function to explicitly request camera permission
    const requestCameraPermission = async () => {
        try {
            // Attempt to get user media (camera stream).
            // We don't stop the tracks immediately; html5-qrcode will manage the stream.
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            // If successful, stop the stream immediately as we just need to check permission
            // This is a common pattern to trigger the permission prompt without keeping the camera active unnecessarily
            stream.getTracks().forEach(track => track.stop());
            return true; // Permission granted
        } catch (err) {
            console.error("Camera permission denied or error:", err);
            let errorMessage = "Camera access is required for scanning. Please enable camera permissions for your browser in your phone's settings.";
            if (err.name === "NotAllowedError") {
                errorMessage = "Camera permission was denied. Please go to your phone's Settings > Apps > [Your Browser] > Permissions and enable Camera access for this site. Then refresh this page.";
            } else if (err.name === "NotFoundError") {
                errorMessage = "No camera found on your device.";
            } else if (err.name === "NotReadableError") {
                errorMessage = "Camera is already in use by another application. Please close other apps using the camera and try again.";
            }
            showCustomModal(errorMessage);
            setStatus("Camera permission denied.");
            return false; // Permission denied or error
        }
    };

    // Effect for Firebase initialization and authentication listener
    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const firestoreDb = getFirestore(app);
            const firebaseAuth = getAuth(app);

            setDb(firestoreDb);
            setAuth(firebaseAuth);

            // Listen for auth state changes to get the current user's UID
            const authUnsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
                if (user) {
                    setUserId(user.uid);
                    setStatus('Authenticated. Requesting camera access...');
                } else {
                    // No user signed in, attempt anonymous sign-in
                    try {
                        // Use initialAuthToken if available (from Canvas environment), otherwise sign in anonymously
                        if (typeof initialAuthToken !== 'undefined' && initialAuthToken) {
                            await signInWithCustomToken(firebaseAuth, initialAuthToken);
                        } else {
                            await signInAnonymously(firebaseAuth);
                        }
                    } catch (error) {
                        console.error("Error signing in:", error);
                        setStatus(`Authentication failed: ${error.message}`);
                        showCustomModal(`Authentication failed: ${error.message}`);
                    }
                }
                setIsAuthReady(true); // Mark authentication as ready
            });

            // Cleanup function for this effect: unsubscribe from auth changes
            return () => {
                authUnsubscribe();
                // Also ensure scanner is cleared on unmount
                if (html5QrcodeScannerRef.current && typeof html5QrcodeScannerRef.current.clear === 'function') {
                    html5QrcodeScannerRef.current.clear().catch(error => {
                        console.error("Failed to clear scanner on unmount:", error);
                    });
                }
            };
        } catch (error) {
            console.error("Firebase initialization failed:", error);
            setStatus(`Initialization failed: ${error.message}`);
            showCustomModal(`Initialization failed: ${error.message}`);
        }
    }, []); // Empty dependency array means this effect runs once on mount

    // Effect to initialize the QR code scanner
    useEffect(() => {
        if (isAuthReady && userId && !isScannerReady) {
            const initScanner = async () => {
                // Request camera permission before attempting to load/render the scanner
                const hasPermission = await requestCameraPermission();
                if (!hasPermission) {
                    return; // Stop if no camera permission
                }

                // Load html5-qrcode library dynamically
                loadScript('https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.4/html5-qrcode.min.js')
                    .then(() => {
                        // Ensure the global objects from the library are available
                        if (window.Html5QrcodeScanner && window.Html5QrcodeSupportedFormats) {
                            // Create the scanner instance only once
                            if (!html5QrcodeScannerRef.current) {
                                html5QrcodeScannerRef.current = new window.Html5QrcodeScanner(
                                    scannerId, // ID of the HTML element to render the scanner into
                                    {
                                        fps: 10, // Frames per second for scanning
                                        qrbox: { width: 250, height: 250 }, // Size of the scanning box
                                        // Explicitly prefer the environment (back) camera for better scanning
                                        videoConstraints: {
                                            facingMode: { exact: "environment" }
                                        },
                                        disableFlip: false, // Allows fallback to user (front) camera if environment not found
                                        formatsToSupport: [
                                            window.Html5QrcodeSupportedFormats.QR_CODE,
                                            window.Html5QrcodeSupportedFormats.CODE_128,
                                            window.Html5QrcodeSupportedFormats.EAN_13,
                                        ]
                                    },
                                    /* verbose= */ false // Set to true for more detailed console logs from the scanner
                                );
                            }

                            // Callback function for successful scan
                            const onScanSuccess = async (decodedText, decodedResult) => {
                                console.log(`Code matched = ${decodedText}`, decodedResult);
                                setScanResult(decodedText);
                                setStatus(`Scanned: ${decodedText}`);

                                // If not yet paired, attempt to parse the scanned text as a pairing code
                                if (!desktopPairingCode || !pairedDesktopUserId) {
                                    // Expected pairing format: PAIRINGCODE|DESKTOPUSERID
                                    const parts = decodedText.split('|');
                                    // Validate format: 2 parts, 6-char alphanumeric code, and non-empty user ID
                                    if (parts.length === 2 && /^[A-Z0-9]{6}$/.test(parts[0]) && parts[1].length > 0) {
                                        const scannedPairingCode = parts[0];
                                        const scannedDesktopUserId = parts[1];

                                        if (db && userId) {
                                            // Construct the Firestore document reference using the SCANNED desktopUserId
                                            const pairingDocRef = doc(db, `artifacts/${APP_IDENTIFIER}/users/${scannedDesktopUserId}/pairing_sessions`, scannedPairingCode);
                                            try {
                                                const docSnap = await getDoc(pairingDocRef);
                                                // Verify the document exists AND the desktopUserId in the document matches the one we scanned
                                                if (docSnap.exists() && docSnap.data().desktopUserId === scannedDesktopUserId) {
                                                    setDesktopPairingCode(scannedPairingCode);
                                                    setPairedDesktopUserId(scannedDesktopUserId); // Store the desktop's user ID for future writes
                                                    setStatus(`Paired with desktop using code: ${scannedPairingCode}. You can now scan textbook barcodes.`);
                                                    showCustomModal(`Paired with desktop using code: ${scannedPairingCode}. You can now scan textbook barcodes.`);
                                                    // Pause the scanner after successful pairing
                                                    if (html5QrcodeScannerRef.current) {
                                                        html5QrcodeScannerRef.current.pause(true);
                                                    }
                                                } else {
                                                    showCustomModal("Scanned pairing code invalid or not associated with a valid desktop session. Please scan the correct QR code from your desktop receiver.");
                                                    setStatus("Invalid pairing code scanned. Please try again.");
                                                }
                                            } catch (error) {
                                                console.error("Error verifying scanned pairing code:", error);
                                                showCustomModal(`Error verifying pairing code: ${error.message}`);
                                                setStatus(`Error verifying pairing: ${error.message}`);
                                            }
                                        }
                                    } else {
                                        showCustomModal("Scanned code does not look like a valid pairing QR code. Please scan the QR code from your desktop receiver (it should be CODE|USERID).");
                                        setStatus("Invalid QR code scanned for pairing. Please try again.");
                                    }
                                } else {
                                    // If already paired, send the scanned URL to Firestore
                                    if (db && pairedDesktopUserId && desktopPairingCode) {
                                        // Use the stored pairedDesktopUserId for the Firestore path
                                        const pairingDocRef = doc(db, `artifacts/${APP_IDENTIFIER}/users/${pairedDesktopUserId}/pairing_sessions`, desktopPairingCode);
                                        try {
                                            await updateDoc(pairingDocRef, {
                                                scannedUrl: decodedText,
                                                timestamp: new Date().toISOString(),
                                                mobileUserId: userId, // Mobile's own userId for tracking
                                                status: 'scanned'
                                            });
                                            setStatus(`URL sent to desktop: ${decodedText}`);
                                            showCustomModal(`URL sent to desktop: ${decodedText}`);
                                        } catch (error) {
                                            console.error("Error sending URL to Firestore:", error);
                                            setStatus(`Failed to send URL: ${error.message}`);
                                            showCustomModal(`Failed to send URL: ${error.message}`);
                                        }
                                    } else {
                                        setStatus("Not connected to desktop. Please pair first.");
                                        showCustomModal("Not connected to desktop. Please pair first.");
                                    }
                                }
                            };

                            // Callback function for scan errors (e.g., no QR code detected)
                            const onScanError = (errorMessage) => {
                                // console.warn(errorMessage); // Log errors, but don't show to user constantly as it can be noisy
                            };

                            // Render the scanner into the designated HTML element
                            html5QrcodeScannerRef.current.render(onScanSuccess, onScanError);
                            setIsScannerReady(true);
                            setStatus('Scanner ready. Waiting for pairing code or textbook barcode.');
                        } else {
                            console.error("Html5QrcodeScanner or Html5QrcodeSupportedFormats not found on window object.");
                            setStatus("Scanner library not loaded correctly.");
                            showCustomModal("Scanner library not loaded correctly. Please refresh the page.");
                        }
                    })
                    .catch(error => {
                        console.error("Failed to load html5-qrcode.min.js:", error);
                        setStatus(`Failed to load scanner: ${error.message}`);
                        showCustomModal(`Failed to load scanner: ${error.message}`);
                    });
            };

            initScanner(); // Call the async function to initialize scanner when component mounts or dependencies change

            // Cleanup function for this effect: clear the scanner when component unmounts
            return () => {
                if (html5QrcodeScannerRef.current && typeof html5QrcodeScannerRef.current.clear === 'function') {
                    html5QrcodeScannerRef.current.clear().catch(error => {
                        console.error("Failed to clear scanner on unmount:", error);
                    });
                }
            };
        }
    }, [isAuthReady, userId, isScannerReady, desktopPairingCode, pairedDesktopUserId, db]); // Dependencies for scanner effect

    // Handler for manual pairing code submission
    const handleManualPairing = async () => {
        const input = manualPairingInput.trim().toUpperCase();
        if (!input) {
            showCustomModal("Please enter the full pairing string (e.g., CODE|USERID).");
            return;
        }

        // Validate the input format for manual pairing
        const parts = input.split('|');
        if (parts.length !== 2 || !/^[A-Z0-9]{6}$/.test(parts[0]) || parts[1].length === 0) {
            showCustomModal("Invalid pairing string format. It should be CODE|USERID (e.g., HMHWXL|btWs41oyQfQ1EZr3MhPn9UL5OzY2).");
            return;
        }

        const code = parts[0];
        const desktopId = parts[1];

        if (db && userId) {
            // Use the desktopId from the input for the Firestore path
            const pairingDocRef = doc(db, `artifacts/${APP_IDENTIFIER}/users/${desktopId}/pairing_sessions`, code);
            try {
                // Verify the pairing code exists and belongs to the specified desktop user
                const docSnap = await getDoc(pairingDocRef);
                if (docSnap.exists() && docSnap.data().desktopUserId === desktopId) {
                    setDesktopPairingCode(code);
                    setPairedDesktopUserId(desktopId); // Store the desktop's user ID
                    setStatus(`Manually paired with desktop using code: ${code}.`);
                    showCustomModal(`Successfully paired with desktop using code: ${code}. You can now scan textbook barcodes.`);
                    // Pause scanner if it was active
                    if (html5QrcodeScannerRef.current && html5QrcodeScannerRef.current.isScanning) {
                         html5QrcodeScannerRef.current.pause(true);
                    }
                } else {
                    showCustomModal("Pairing code invalid or not associated with your desktop session. Please check the code and try again.");
                    setStatus('Manual pairing failed. Please try again.');
                }
            } catch (error) {
                console.error("Error verifying manual pairing code:", error);
                showCustomModal(`Error verifying pairing code: ${error.message}`);
                setStatus(`Manual pairing failed: ${error.message}`);
            }
        }
    };

    // Handler for "Scan New Barcode" button click
    const handleScanNewBarcode = () => {
        if (desktopPairingCode && pairedDesktopUserId) {
            setScanResult(''); // Clear previous scan result
            setStatus('Ready to scan next textbook barcode...');
            // Resume the scanner if it's paused
            if (html5QrcodeScannerRef.current && typeof html5QrcodeScannerRef.current.resume === 'function') {
                html5QrcodeScannerRef.current.resume();
            }
        } else {
            showCustomModal("Please pair with your desktop first.");
        }
    };

    // Handler for "Reset Pairing" button click
    const handleResetPairing = () => {
        setDesktopPairingCode('');
        setPairedDesktopUserId(''); // Clear paired desktop user ID
        setScanResult('');
        setManualPairingInput('');
        setStatus('Pairing reset. Please scan desktop QR or enter pairing code to re-pair.');
        // Resume scanner for pairing
        if (html5QrcodeScannerRef.current && typeof html5QrcodeScannerRef.current.resume === 'function') {
            html5QrcodeScannerRef.current.resume();
        }
    };

    // Render the UI for the Mobile Scanner
    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-500 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center transform transition-all duration-300 hover:scale-105">
                <h1 className="text-4xl font-extrabold text-purple-800 mb-4 tracking-tight">
                    Roboshakti Mobile Scanner
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                    Scan barcodes to open content on your paired desktop.
                </p>

                {userId && (
                    <p className="text-sm text-gray-500 mb-4">
                        Your Mobile User ID: <span className="font-mono text-gray-700 break-all">{userId}</span>
                    </p>
                )}

                {/* Conditional rendering based on whether the app is paired or not */}
                {(!desktopPairingCode || !pairedDesktopUserId) ? (
                    <div className="mb-8">
                        <p className="text-xl font-bold text-gray-800 mb-4">
                            Pair with your Desktop Receiver
                        </p>
                        <div className="mb-4">
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-lg text-gray-800 text-sm md:text-base bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Enter CODE|USERID from desktop"
                                value={manualPairingInput}
                                onChange={(e) => setManualPairingInput(e.target.value)}
                            />
                            <button
                                onClick={handleManualPairing}
                                className="mt-3 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 w-full"
                            >
                                Manually Pair
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4">OR</p>
                        <p className="text-gray-800 font-semibold mb-4">
                            Scan the QR code displayed on your Desktop Receiver.
                        </p>
                    </div>
                ) : (
                    <div className="mb-8">
                        <p className="text-xl font-bold text-gray-800 mb-4">
                            Paired with Desktop: <span className="text-purple-700 break-all">{desktopPairingCode} ({pairedDesktopUserId})</span>
                        </p>
                        <button
                            onClick={handleScanNewBarcode}
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 mb-4"
                        >
                            Scan New Barcode
                        </button>
                        <button
                            onClick={handleResetPairing}
                            className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ml-2"
                        >
                            Reset Pairing
                        </button>
                    </div>
                )}

                {/* Barcode scanner element - this is where the camera feed will appear */}
                <div id={scannerId} className="w-full h-auto bg-gray-200 rounded-lg overflow-hidden mb-6">
                    {/* The scanner will render here */}
                </div>

                <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded-lg mb-6" role="alert">
                    <p className="font-bold">Status:</p>
                    <p>{status}</p>
                </div>

                {scanResult && (
                    <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-lg mt-6" role="alert">
                        <p className="font-bold">Last Scanned Result:</p>
                        <p className="break-all">{scanResult}</p>
                    </div>
                )}

                {/* Custom Modal for alerts */}
                {showModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full text-center">
                            <h3 className="text-xl font-bold text-red-600 mb-4">Information</h3>
                            <p className="text-gray-700 mb-6">{modalMessage}</p>
                            <button
                                onClick={() => setShowModal(false)}
                                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
